import { useMemo } from 'react';

import {
  Box,
  BoxProps,
  Card,
  CardProps,
  CollapsibleSwitchCardProps,
  Group,
  GroupProps,
  Stack,
  SwitchCard,
  SwitchCardProps,
  Text,
  ThemeIcon,
  UnstyledButton,
  UnstyledButtonProps,
  cx,
} from 'ui';
import { IconChevronRight } from 'ui/Icons';

import classes from './ClickableOptionsCard.module.css';

type ClickableOptionsCardProps = Omit<CardProps, 'withBorder'> & {
  variant?: 'default' | 'gray' | 'danger';
};

const ClickableOptionsCard = ({
  children,
  className,
  variant,
  mod,
  ...props
}: ClickableOptionsCardProps) => {
  return (
    <Card
      className={cx('ClickableOptionsCard', classes.ClickableOptionsCard, className)}
      radius="md"
      mod={{ variant, ...mod }}
      {...props}
    >
      <Stack className={classes.ClickableOptionsCard__stack}>{children}</Stack>
    </Card>
  );
};

const ChevronRightIcon = () => {
  return (
    <ThemeIcon size="sm" variant="transparent" c="gray.4">
      <IconChevronRight size={16} />
    </ThemeIcon>
  );
};

interface ClickableOptionsRowPropsBase extends UnstyledButtonProps {
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  onClick?: () => void;
  title: React.ReactNode | string;
  subtitle?: React.ReactNode | string;
  /**
   * Use variant on the row only when not within a ClickableOptionsCard
   */
  variant?: 'default' | 'gray';
  children?: React.ReactNode;
  active?: boolean;
  groupProps?: GroupProps & { component?: React.ElementType };
  isHoverStylesActive?: boolean;
}

type ClickableOptionsRowProps =
  | (ClickableOptionsRowPropsBase & {
      /**
       * If href is provided, the row will be rendered as an anchor tag and the onClick prop will be ignored.
       */
      href: string;
      disabled?: never;
    })
  | (ClickableOptionsRowPropsBase & {
      href?: never;
      disabled?: boolean;
    });

const ClickableOptionsRow = ({
  leftSection,
  rightSection,
  onClick,
  title,
  className,
  subtitle,
  variant = 'default',
  children,
  active = false,
  groupProps = {},
  isHoverStylesActive = false,
  disabled = false,
  href,
  mod,
}: ClickableOptionsRowProps) => {
  const combinedGroupProps = useMemo(() => {
    const { mod: groupMod, ...restGroupProps } = groupProps;

    const existingProps: GroupProps & {
      component?: React.ElementType;
      href?: string;
      target?: string;
      mod?: BoxProps['mod'];
    } = {
      component: !disabled && onClick ? UnstyledButton : undefined,
      onClick: !disabled ? onClick : undefined,
      mod: [
        groupMod,
        {
          clickable: !disabled && (!!onClick || !!href),
          variant,
          active,
          disabled: href ? false : disabled,
          'is-hover-styles-active': isHoverStylesActive,
        },
      ],
      ...restGroupProps,
    };

    if (href) {
      existingProps.component = 'a';
      existingProps.href = href;
      existingProps.target = '_blank';
      existingProps.onClick = undefined;
      existingProps.rel = 'noopener noreferrer';
    }

    return existingProps;
  }, [groupProps, href, onClick, disabled, isHoverStylesActive, variant, active]);

  return (
    <Box className={cx(classes.ClickableOptionsCard__rowWrapper, className)} mod={mod}>
      <Group className={classes.ClickableOptionsCard__row} {...combinedGroupProps}>
        {leftSection}
        <Stack className={classes.ClickableOptionsCard__rowCenter}>
          {typeof title === 'string' ? (
            <Text fz="sm" fw={500} ta="left">
              {title}
            </Text>
          ) : (
            title
          )}
          {typeof subtitle === 'string' ? (
            <Text fz="xxxs" fw={400} ta="left" c="gray">
              {subtitle}
            </Text>
          ) : (
            subtitle
          )}
        </Stack>
        {rightSection ?? <ChevronRightIcon />}
      </Group>
      {children}
    </Box>
  );
};

const ClickableOptionsCardSwitch = ({
  children,
  className,
  ...props
}: { children?: React.ReactNode } & SwitchCardProps) => {
  return (
    <SwitchCard className={cx(classes.ClickableOptionsCard__rowWrapper, className)} {...props}>
      {children}
    </SwitchCard>
  );
};

const ClickableOptionsCardSwitchCollapsible = ({
  children,
  className,
  ...props
}: { children: React.ReactNode } & CollapsibleSwitchCardProps) => {
  return (
    <SwitchCard.Collapsible
      className={cx(classes.ClickableOptionsCard__rowWrapper, className)}
      {...props}
    >
      {children}
    </SwitchCard.Collapsible>
  );
};

ClickableOptionsCard.Row = ClickableOptionsRow;
ClickableOptionsCard.Switch = ClickableOptionsCardSwitch;
ClickableOptionsCard.SwitchCollapsible = ClickableOptionsCardSwitchCollapsible;

export { ClickableOptionsCard };
